import type { SocialPlatform } from "@brandex/api-client";

/** Platforms with a social panel, in the order the server lists them. */
export const SOCIAL_KEYS = ["youtube", "facebook", "instagram", "tiktok", "linkedin", "google"] as const;
export type SocialKey = (typeof SOCIAL_KEYS)[number];

export function isSocialKey(v: unknown): v is SocialKey {
  return typeof v === "string" && (SOCIAL_KEYS as readonly string[]).includes(v);
}

/** Who does a guide step: the doctor, or whoever is connecting. */
export type Who = "doctor" | "either";

/** Met, not met, or not known yet (nothing to check before connecting). */
export type StepState = "done" | "todo" | "unknown";

export interface GuideStep {
  /** `social.guide.<platform>.<id>.title` / `.body`. */
  id: string;
  who: Who;
  /** The platform's own help on this step. */
  help?: string;
  /** Whether the step is done, from what the panel knows. */
  state?: (p: SocialPlatform) => StepState;
}

export interface Guide {
  /** Connects by pasting a link (YouTube); the rest sign in. */
  byLink: boolean;
  steps: GuideStep[];
}

const connected = (p: SocialPlatform): StepState => (p.connected ? "done" : "todo");
const loaded = (p: SocialPlatform): StepState => (p.sync?.history_loaded === true ? "done" : p.connected ? "todo" : "unknown");
const pinned = (p: SocialPlatform): StepState => (p.pinned > 0 ? "done" : p.connected ? "todo" : "unknown");

/**
 * The connection guide on each platform's tab (owner, 2026-11-04). Help
 * links open the platforms' own help search, which stays valid when they
 * reorganise their articles. Last checked 2026-11-04.
 */
export const GUIDES: Record<SocialKey, Guide> = {
  youtube: {
    byLink: true,
    steps: [
      { id: "find", who: "either", help: "https://support.google.com/youtube/search?q=find+your+channel+url" },
      { id: "paste", who: "either", state: connected },
      { id: "sync", who: "either", state: loaded },
      { id: "choose", who: "either", state: pinned },
    ],
  },
  facebook: {
    byLink: false,
    steps: [
      { id: "page", who: "doctor", help: "https://www.facebook.com/help/search/?query=create%20a%20page" },
      { id: "control", who: "doctor", help: "https://www.facebook.com/help/search/?query=page%20access%20full%20control" },
      { id: "connect", who: "either", state: connected },
      { id: "sync", who: "either", state: loaded },
    ],
  },
  instagram: {
    byLink: false,
    steps: [
      { id: "professional", who: "doctor", help: "https://help.instagram.com/search?query=switch%20to%20professional%20account" },
      { id: "link", who: "doctor", help: "https://help.instagram.com/search?query=connect%20facebook%20page" },
      { id: "connect", who: "either", state: connected },
      { id: "sync", who: "either", state: loaded },
    ],
  },
  tiktok: {
    byLink: false,
    steps: [
      { id: "login", who: "doctor" },
      { id: "connect", who: "either", state: connected },
      { id: "renew", who: "either" },
    ],
  },
  linkedin: {
    byLink: false,
    steps: [
      { id: "page", who: "doctor", help: "https://www.linkedin.com/help/linkedin/search?keywords=create%20a%20page" },
      { id: "admin", who: "doctor", help: "https://www.linkedin.com/help/linkedin/search?keywords=page%20admin%20roles" },
      { id: "connect", who: "either", state: connected },
    ],
  },
  google: {
    byLink: false,
    steps: [
      { id: "profile", who: "doctor", help: "https://support.google.com/business/search?q=verify+your+business" },
      { id: "manager", who: "doctor", help: "https://support.google.com/business/search?q=add+owners+and+managers" },
      { id: "connect", who: "either", state: connected },
      { id: "sync", who: "either", state: loaded },
    ],
  },
};

/** Sync problem codes from the server, each explained in "Common problems". */
export const PROBLEMS = ["auth", "not_found", "rate_limited", "not_configured", "unavailable"] as const;
export type Problem = (typeof PROBLEMS)[number];

export function isProblem(v: unknown): v is Problem {
  return typeof v === "string" && (PROBLEMS as readonly string[]).includes(v);
}
